import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { MATH_DOMAINS } from '../../data/domains';
import { Standard } from '../../data/types';
import { AuthContext } from '../../App';
import { dataService } from '../../services/dataService';
import { RoleBasedComponent, RoleBasedButton } from '../common/RoleBasedComponent';
import { usePermissions } from '../../contexts/PermissionContext';
import PageContainer from '../layouts/PageContainer';
import ContentContainer from '../layouts/ContentContainer';

// Import Standards Modal Component
const ImportStandardsModal = ({ onClose, onSubmit }: { onClose: () => void; onSubmit: () => void }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 w-full max-w-md">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Import Standards</h3>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload CSV File
        </label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
          <div className="space-y-1 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex text-sm text-gray-600">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-700 focus-within:outline-none"
              >
                <span>Upload a file</span>
                <input id="file-upload" name="file-upload" type="file" className="sr-only" />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">CSV up to 10MB</p>
          </div>
        </div>
      </div>
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Import
        </button>
      </div>
    </div>
  </div>
);

// Create Standard Modal Component
interface StandardFormData {
  code: string;
  description: string;
  domainId: string;
  grade: string;
  cluster: string;
}

const CreateStandardModal = ({ onClose, onSubmit }: { 
  onClose: () => void; 
  onSubmit: (data: Omit<StandardFormData, 'grade'>) => void 
}) => {
  const [formData, setFormData] = useState<StandardFormData>({
    code: '',
    description: '',
    domainId: '',
    grade: '',
    cluster: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Remove grade from the submitted data as it's not part of the Standard type
    const { grade, ...standardData } = formData;
    onSubmit(standardData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Standard</h3>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-1">
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                Standard Code
              </label>
              <input
                type="text"
                name="code"
                id="code"
                required
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., 3.NF.A.1"
                value={formData.code}
                onChange={handleChange}
              />
            </div>
            <div className="col-span-1">
              <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-1">
                Grade Level
              </label>
              <select
                id="grade"
                name="grade"
                required
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                value={formData.grade}
                onChange={handleChange}
              >
                <option value="">Select Grade</option>
                <option value="K">Kindergarten</option>
                {[1, 2, 3, 4, 5, 6, 7, 8].map(grade => (
                  <option key={grade} value={grade.toString()}>
                    Grade {grade}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label htmlFor="domainId" className="block text-sm font-medium text-gray-700 mb-1">
                Domain
              </label>
              <select
                id="domainId"
                name="domainId"
                required
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                value={formData.domainId}
                onChange={handleChange}
              >
                <option value="">Select Domain</option>
                {MATH_DOMAINS.map(domain => (
                  <option key={domain.id} value={domain.id}>
                    {domain.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label htmlFor="cluster" className="block text-sm font-medium text-gray-700 mb-1">
                Cluster
              </label>
              <input
                type="text"
                name="cluster"
                id="cluster"
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., Operations & Algebraic Thinking"
                value={formData.cluster}
                onChange={handleChange}
              />
            </div>
            <div className="col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                required
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Enter the standard description"
                value={formData.description}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="mt-5 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main StandardsScreen Component
const StandardsScreen: React.FC = () => {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { activeRole } = useContext(AuthContext);
  
  const [standards, setStandards] = useState<Standard[]>([]);
  const [filteredStandards, setFilteredStandards] = useState<Standard[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Get standards data
  useEffect(() => {
    fetchStandards();
  }, [activeRole]);

  const fetchStandards = async () => {
    try {
      setLoading(true);
      // In a real app, you would call an API to get standards
      const fetchedStandards = await dataService.getStandards();
      
      if (fetchedStandards) {
        setStandards(fetchedStandards);
        setFilteredStandards(fetchedStandards);
      } else {
        console.error('Failed to fetch standards data');
      }
    } catch (error) {
      console.error('Error fetching standards:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter standards based on search query and domain filter
  useEffect(() => {
    let result = standards;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(standard => 
        standard.code.toLowerCase().includes(query) || 
        standard.description.toLowerCase().includes(query)
      );
    }
    
    if (domainFilter) {
      result = result.filter(standard => standard.domainId === domainFilter);
    }
    
    setFilteredStandards(result);
  }, [searchQuery, domainFilter, standards]);

  // Handle import submission
  const handleImportSubmit = () => {
    // In a real app, you would process the file upload here
    // For this demo, we'll just close the modal and show a success notification
    setShowImportModal(false);
    // Show success notification (not implemented in this demo)
    console.log('Standards imported successfully');
    // Refresh the standards list
    fetchStandards();
  };

  // Handle create standard submission
  const handleCreateSubmit = (formData: Omit<StandardFormData, 'grade'>) => {
    // In a real app, you would call an API to create the standard
    // For this demo, we'll just create a mock standard and add it to the list
    const newStandard: Standard = {
      id: `std-${Math.random().toString(36).substr(2, 9)}`,
      code: formData.code,
      description: formData.description,
      domainId: formData.domainId,
      cluster: formData.cluster || '',
      grade: '3', // Default grade
      prerequisites: [], // Empty prerequisites array
    };
    
    // Add the new standard to the list
    setStandards([...standards, newStandard]);
    
    // Close the modal
    setShowCreateModal(false);
    
    // Show success notification (not implemented in this demo)
    console.log('Standard created successfully');
  };
  
  // Handle view details
  const handleViewDetails = (standardId: string) => {
    navigate(`/standards/${standardId}`);
  };

  return (
    <PageContainer title="Standards" userRole="Teacher">
      <ContentContainer>
        <div className="flex flex-col lg:flex-row justify-between mb-6">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-4 lg:mb-0">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search standards..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full sm:w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            
            {/* Domain Filter */}
            <div>
              <select
                className="w-full sm:w-64 border border-gray-300 rounded-md px-3 py-2"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
              >
                <option value="">All Domains</option>
                {MATH_DOMAINS.map((domain) => (
                  <option key={domain.id} value={domain.id}>
                    {domain.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex space-x-4">
            <RoleBasedButton
              resource="standards"
              requiredPermission="create"
              onClick={() => setShowCreateModal(true)}
              tooltip="You don't have permission to create standards"
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
            >
              Create Standard
            </RoleBasedButton>
            
            <RoleBasedButton
              resource="standards"
              requiredPermission="view"
              onClick={() => setShowImportModal(true)}
              tooltip="You don't have permission to import standards"
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium bg-primary text-white hover:bg-primary-dark"
            >
              Import Standards
            </RoleBasedButton>
          </div>
        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="spinner"></div>
          </div>
        )}

        {/* Table */}
        {!loading && (
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Domain
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStandards.map(standard => (
                <tr key={standard.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">
                    {standard.code}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {standard.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {MATH_DOMAINS.find(d => d.id === standard.domainId)?.name || ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <RoleBasedComponent
                      resource="standards"
                      requiredPermission="view"
                    >
                      <button 
                        className="text-primary hover:text-primary-700 mr-3"
                        onClick={() => handleViewDetails(standard.id)}
                      >
                        View Details
                      </button>
                    </RoleBasedComponent>
                    
                    <RoleBasedComponent
                      resource="learning-paths"
                      requiredPermission="edit"
                      tooltip="You don't have permission to add standards to learning paths"
                    >
                      <button className="text-primary hover:text-primary-700">
                        Add to Path
                      </button>
                    </RoleBasedComponent>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        
        {!loading && filteredStandards.length === 0 && (
          <div className="text-center py-12">
            <svg 
              className="mx-auto h-12 w-12 text-gray-400" 
              fill="none" 
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1} 
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No standards found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter to find what you're looking for.</p>
          </div>
        )}
      </ContentContainer>
      
      {/* Modals */}
      {showImportModal && (
        <ImportStandardsModal 
          onClose={() => setShowImportModal(false)}
          onSubmit={handleImportSubmit}
        />
      )}
      {showCreateModal && (
        <CreateStandardModal 
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateSubmit}
        />
      )}
    </PageContainer>
  );
};

export default StandardsScreen;
